import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

import {
  formatAUD,
  formatPrice,
  formatDateTime,
  formatMWh,
  useContainerWidth,
  ChartSection,
  StatCard,
  CHART_MARGINS,
  CHART_MARGINS_SIMPLE,
  defaultLegendProps,
  defaultLineLegendProps,
  tickLabelStyle,
  rotatedTickLabelStyle,
} from './charts/shared';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const inputDateToInt = (s) => {
  if (!s) return null;
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  return parseInt(`${parts[0]}${parts[1]}${parts[2]}0000`, 10);
};

// ── Tranche colors ──────────────────────────────────────────────────────────

const TRANCHE_COLORS = [
  '#D98A2B', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

// ── Revenue Comparison Area Chart ───────────────────────────────────────────

const RevenueComparisonChart = ({ data, frequency }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return null;

  const xLabels = data.map(d => formatDateTime(d.bucket, frequency));
  const spotSeries = data.map(d => d.spot_revenue);
  const blendedSeries = data.map(d => d.blended_revenue);

  return (
    <Box ref={ref} sx={{ width: '100%', height: 380, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <LineChart
          width={width}
          height={380}
          series={[
            {
              label: 'Spot Only Revenue',
              data: spotSeries,
              color: '#94a3b8',
              area: true,
              curve: 'monotoneX',
            },
            {
              label: 'Blended Revenue (PPA + Merchant)',
              data: blendedSeries,
              color: 'primary.main',
              area: true,
              curve: 'monotoneX',
            },
          ]}
          xAxis={[{
            scaleType: 'band',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatAUD, tickLabelStyle }]}
          margin={CHART_MARGINS}
          slotProps={{ legend: defaultLineLegendProps }}
        />
      )}
    </Box>
  );
};

// ── Revenue Breakdown Stacked Bar ───────────────────────────────────────────

const RevenueBreakdownChart = ({ data, frequency }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return null;

  const xLabels = data.map(d => formatDateTime(d.bucket, frequency));

  return (
    <Box ref={ref} sx={{ width: '100%', height: 380, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <BarChart
          width={width}
          height={380}
          series={[
            {
              label: 'PPA Revenue',
              data: data.map(d => d.ppa_revenue),
              stack: 'revenue',
              color: 'primary.main',
            },
            {
              label: 'Merchant Revenue',
              data: data.map(d => d.merchant_revenue),
              stack: 'revenue',
              color: '#94a3b8',
            },
          ]}
          xAxis={[{
            scaleType: 'band',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatAUD, tickLabelStyle }]}
          margin={CHART_MARGINS}
          slotProps={{ legend: defaultLegendProps }}
        />
      )}
    </Box>
  );
};

// ── Price Comparison Line Chart ─────────────────────────────────────────────

const PriceComparisonChart = ({ data, frequency, weightedPpaPrice }) => {
  const { ref, width } = useContainerWidth();

  if (!data || data.length === 0) return null;

  const xLabels = data.map(d => formatDateTime(d.bucket, frequency));

  const series = [
    {
      label: 'Spot Price',
      data: data.map(d => d.spot_price),
      color: '#94a3b8',
      curve: 'monotoneX',
    },
    {
      label: 'Blended Price',
      data: data.map(d => d.blended_price),
      color: 'primary.main',
      curve: 'monotoneX',
    },
  ];

  if (weightedPpaPrice > 0) {
    series.push({
      label: `PPA Price (A$${weightedPpaPrice.toFixed(2)}/MWh)`,
      data: data.map(() => weightedPpaPrice),
      color: '#f59e0b',
      curve: 'linear',
    });
  }

  return (
    <Box ref={ref} sx={{ width: '100%', height: 380, minWidth: 0, overflow: 'hidden' }}>
      {width > 0 && (
        <LineChart
          width={width}
          height={380}
          series={series}
          xAxis={[{
            scaleType: 'band',
            data: xLabels,
            tickLabelStyle: rotatedTickLabelStyle,
            tickNumber: xLabels.length > 30 ? 30 : undefined,
          }]}
          yAxis={[{ valueFormatter: formatPrice, tickLabelStyle }]}
          margin={CHART_MARGINS_SIMPLE}
          slotProps={{ legend: defaultLineLegendProps }}
        />
      )}
    </Box>
  );
};

// ── Tranche Editor Row ──────────────────────────────────────────────────────

const TrancheRow = ({ tranche, index, onChange, onRemove, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 1.5,
      px: 2,
      borderRadius: '10px',
      bgcolor: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      transition: 'border-color 0.2s ease',
      '&:hover': { borderColor: 'rgba(217, 138, 43, 0.20)' },
    }}
  >
    <Box
      sx={{
        width: 4,
        height: 40,
        borderRadius: 1,
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <TextField
      size="small"
      placeholder="Tranche name"
      value={tranche.name}
      onChange={(e) => onChange(index, 'name', e.target.value)}
      sx={{ width: 150, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
    />
    <TextField
      size="small"
      label="A$/MWh"
      type="number"
      value={tranche.price}
      onChange={(e) => onChange(index, 'price', parseFloat(e.target.value) || 0)}
      sx={{ width: 100, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
      inputProps={{ min: 0, step: 0.5 }}
    />
    <Box sx={{ flex: 1, minWidth: 120, px: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.5 }}>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
          Coverage
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'primary.main', fontWeight: 700 }}>
          {tranche.coverage_pct}%
        </Typography>
      </Box>
      <Slider
        value={tranche.coverage_pct}
        onChange={(_, v) => onChange(index, 'coverage_pct', v)}
        min={1}
        max={100}
        size="small"
        sx={{
          color: color,
          '& .MuiSlider-thumb': { width: 14, height: 14 },
          '& .MuiSlider-track': { height: 4 },
          '& .MuiSlider-rail': { height: 4, bgcolor: 'rgba(255, 255, 255, 0.08)' },
        }}
      />
    </Box>
    <IconButton size="small" onClick={() => onRemove(index)} sx={{ color: 'text.secondary', '&:hover': { color: '#ef4444' } }}>
      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
);

// ── Main Component ──────────────────────────────────────────────────────────

const RevenueAnalysis = () => {
  const { portfolioId } = useParams();

  // Default window: last 30 days
  const { defaultStart, defaultEnd } = useMemo(() => {
    const toISO = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const now = new Date();
    const past = new Date();
    past.setDate(now.getDate() - 30);
    return { defaultStart: toISO(past), defaultEnd: toISO(now) };
  }, []);

  // Controls
  const [frequency, setFrequency] = useState('DAILY');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  // Tranches
  const [tranches, setTranches] = useState([
    { name: 'PPA Tranche 1', price: 65, coverage_pct: 25 },
  ]);

  // Simulation state
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState(null);

  // Scenarios
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState('');

  // Import from contracts
  const [importAnchor, setImportAnchor] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [contractsLoaded, setContractsLoaded] = useState(false);

  // Fetch scenarios on mount
  useEffect(() => {
    fetchScenarios();
  }, [portfolioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchScenarios = useCallback(async () => {
    setScenariosLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/revenue/scenarios`);
      setScenarios(res.data.scenarios || []);
    } catch {
      // Silently fail — scenarios panel is secondary
    } finally {
      setScenariosLoading(false);
    }
  }, [portfolioId]);

  const fetchContracts = useCallback(async () => {
    if (contractsLoaded) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/contracts`);
      // New Firestore shape uses `contract_kind` and stores the price either
      // as `terms.ppa.strike_aud_per_mwh` (flat) or as a `price_schedule[]`
      // (per-period rows). We accept either; the import flow below picks
      // whichever is populated.
      const ppaContracts = (res.data.contracts || []).filter((c) => {
        if (c.contract_kind !== 'PPA') return false;
        const ppa = c.terms?.ppa || {};
        return ppa.strike_aud_per_mwh != null
            || (ppa.price_schedule && ppa.price_schedule.length > 0);
      });
      setContracts(ppaContracts);
      setContractsLoaded(true);
    } catch {
      setContracts([]);
    }
  }, [contractsLoaded]);

  // ── Tranche management ──────────────────────────────────────────────────

  const handleTrancheChange = (index, field, value) => {
    setTranches(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const addTranche = () => {
    setTranches(prev => [
      ...prev,
      { name: `PPA Tranche ${prev.length + 1}`, price: 60, coverage_pct: 10 },
    ]);
  };

  const removeTranche = (index) => {
    setTranches(prev => prev.filter((_, i) => i !== index));
  };

  const importFromContract = (contract) => {
    // Read from the new Firestore shape. Prefer the first row of the
    // price_schedule (most representative of current pricing); fall back to
    // flat strike if no schedule exists.
    const ppa = contract.terms?.ppa || {};
    const firstScheduledPrice = ppa.price_schedule?.[0]?.price_aud_per_mwh;
    const price = firstScheduledPrice ?? ppa.strike_aud_per_mwh ?? 60;
    const buyersSharePct = (ppa.buyers_share_pct ?? 1.0) * 100;
    setTranches(prev => [
      ...prev,
      {
        name: contract.project?.name
              || contract.source?.filename?.replace('.pdf', '')
              || 'Imported PPA',
        price,
        // The PPA's buyer share approximates what fraction of generation is
        // covered. Default to 20 (a sensible tranche size) if absent.
        coverage_pct: Number.isFinite(buyersSharePct) ? Math.round(buyersSharePct) : 20,
      },
    ]);
    setImportAnchor(null);
  };

  const totalCoverage = tranches.reduce((sum, t) => sum + t.coverage_pct, 0);

  // ── Simulation ────────────────────────────────────────────────────────────

  const handleSimulate = useCallback(async () => {
    const start = inputDateToInt(startDate);
    const end = inputDateToInt(endDate);
    if (!start || !end) return;

    if (tranches.length === 0) {
      setSimError('Add at least one PPA tranche to simulate');
      return;
    }

    setSimLoading(true);
    setSimError(null);

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/revenue/simulate`,
        { start, end, frequency, tranches }
      );
      setSimResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Simulation failed';
      setSimError(msg);
      setSimResult(null);
    } finally {
      setSimLoading(false);
    }
  }, [portfolioId, startDate, endDate, frequency, tranches]);

  // Auto-run simulation on mount
  useEffect(() => {
    handleSimulate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scenario CRUD ─────────────────────────────────────────────────────────

  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/revenue/scenarios`,
        { name: scenarioName.trim(), tranches }
      );
      setSaveDialogOpen(false);
      setScenarioName('');
      fetchScenarios();
    } catch {
      // Error handled silently
    }
  };

  const handleLoadScenario = (scenario) => {
    setTranches(scenario.tranches || []);
  };

  const handleDeleteScenario = async (scenarioId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/revenue/scenarios/${scenarioId}`
      );
      fetchScenarios();
    } catch {
      // Error handled silently
    }
  };

  // ── Computed values ───────────────────────────────────────────────────────

  const summary = simResult?.summary;
  // Prefer the server-computed weighted PPA price (matches everything the
  // agent + the simulate endpoint see). Fall back to the JS calc only when
  // we don't have a simulate result yet (e.g. tranches edited but not
  // re-simulated).
  const weightedPpaPrice = (summary?.weighted_ppa_price_aud_per_mwh != null)
    ? summary.weighted_ppa_price_aud_per_mwh
    : (totalCoverage > 0
        ? tranches.reduce((s, t) => s + (t.price * t.coverage_pct), 0) / totalCoverage
        : 0);

  const deltaPositive = summary && summary.revenue_delta_aud >= 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Revenue Analysis
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          PPA scenario modeling — compare contracted vs spot market revenue
        </Typography>
      </Box>

      {/* Controls bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          border: '1px solid', borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <TextField
          label="Start"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="End"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <ToggleButtonGroup
          value={frequency}
          exclusive
          onChange={(_, v) => { if (v) setFrequency(v); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2,
              py: 0.6,
              borderColor: 'rgba(255, 255, 255, 0.10)',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: '#F4F2EC',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            },
          }}
        >
          <ToggleButton value="30MIN">30 min</ToggleButton>
          <ToggleButton value="DAILY">Daily</ToggleButton>
          <ToggleButton value="MONTHLY">Monthly</ToggleButton>
          <ToggleButton value="YEARLY">Yearly</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          onClick={handleSimulate}
          disabled={simLoading || tranches.length === 0}
          startIcon={simLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
          sx={{
            px: 3,
            py: 0.75,
            fontWeight: 700,
            fontSize: '0.85rem',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {simLoading ? 'Simulating...' : 'Simulate'}
        </Button>
      </Paper>

      {/* Two-column layout: Scenario Builder + Saved Scenarios */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Scenario Builder */}
        <Paper
          sx={{
            flex: 2,
            p: 2.5,
            border: '1px solid', borderColor: 'divider',
            boxShadow: 'none',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                PPA Tranches
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.2 }}>
                Define contracted price and coverage for each PPA
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Total coverage indicator */}
              <Chip
                label={`${totalCoverage}% covered`}
                size="small"
                icon={totalCoverage > 100 ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : undefined}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  bgcolor: totalCoverage > 100 ? 'rgba(239,68,68,0.1)' : 'rgba(217, 138, 43, 0.08)',
                  color: totalCoverage > 100 ? '#ef4444' : '#D98A2B',
                  borderColor: totalCoverage > 100 ? 'rgba(239,68,68,0.2)' : 'rgba(217, 138, 43, 0.12)',
                  border: '1px solid',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            </Box>
          </Box>

          {/* Coverage progress bar */}
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(totalCoverage, 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: totalCoverage > 100 ? '#ef4444' : '#D98A2B',
                  borderRadius: 3,
                  transition: 'transform 0.4s ease, background-color 0.3s ease',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                0% (Full merchant)
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                100% (Full PPA)
              </Typography>
            </Box>
          </Box>

          {totalCoverage > 100 && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              Total PPA coverage exceeds 100%. Results may not be meaningful.
            </Alert>
          )}

          {/* Tranche list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {tranches.map((t, i) => (
              <TrancheRow
                key={i}
                tranche={t}
                index={i}
                onChange={handleTrancheChange}
                onRemove={removeTranche}
                color={TRANCHE_COLORS[i % TRANCHE_COLORS.length]}
              />
            ))}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={addTranche}
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                color: 'primary.main',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(217, 138, 43, 0.08)' },
              }}
            >
              Add Tranche
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                setImportAnchor(e.currentTarget);
                fetchContracts();
              }}
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' },
              }}
            >
              Import from Contract
            </Button>

            <Menu
              anchorEl={importAnchor}
              open={Boolean(importAnchor)}
              onClose={() => setImportAnchor(null)}
              PaperProps={{
                sx: {
                  maxHeight: 300,
                  minWidth: 260,
                  boxShadow: '0 4px 20px rgba(255, 255, 255, 0.10)',
                  border: '1px solid', borderColor: 'divider',
                },
              }}
            >
              {contracts.length === 0 ? (
                <MenuItem disabled>
                  <ListItemText
                    primary={contractsLoaded ? 'No PPA contracts found' : 'Loading contracts...'}
                    primaryTypographyProps={{ fontSize: '0.85rem', color: 'text.secondary' }}
                  />
                </MenuItem>
              ) : (
                contracts.map((c) => {
                  // Pull the same shape the import-from-contract handler will
                  // use: prefer first row of price_schedule, fall back to flat
                  // strike. This keeps the secondary text consistent with what
                  // gets imported into the tranche.
                  const ppa = c.terms?.ppa || {};
                  const price = ppa.price_schedule?.[0]?.price_aud_per_mwh ?? ppa.strike_aud_per_mwh;
                  const capacity = c.project?.capacity_mw ?? ppa.contracted_mw;
                  const yrs = (ppa.price_schedule?.length ?? 0);
                  return (
                    <MenuItem key={c.contract_id} onClick={() => importFromContract(c)}>
                      <ListItemIcon>
                        <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={c.project?.name || c.source?.filename || c.counterparty}
                        secondary={
                          price != null
                            ? `A$${Number(price).toFixed(2)}/MWh${yrs > 1 ? ` · ${yrs}-yr schedule` : ''}${capacity ? ` · ${capacity} MW` : ''}`
                            : (capacity ? `${capacity} MW · no price set` : 'No price set')
                        }
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </MenuItem>
                  );
                })
              )}
            </Menu>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setSaveDialogOpen(true)}
              disabled={tranches.length === 0}
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' },
              }}
            >
              Save Scenario
            </Button>
          </Box>
        </Paper>

        {/* Saved Scenarios Panel */}
        <Paper
          sx={{
            flex: 1,
            p: 2.5,
            border: '1px solid', borderColor: 'divider',
            boxShadow: 'none',
            minWidth: 240,
            maxHeight: 420,
            overflow: 'auto',
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', mb: 0.3 }}>
            Saved Scenarios
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 2 }}>
            Load or manage your saved PPA scenarios
          </Typography>

          {scenariosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} sx={{ color: 'primary.main' }} />
            </Box>
          ) : scenarios.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <BookmarkBorderIcon sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.10)', mb: 1 }} />
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                No saved scenarios yet
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {scenarios.map((s) => (
                <Box
                  key={s.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'rgba(217, 138, 43, 0.20)',
                      bgcolor: 'rgba(217, 138, 43,0.02)',
                    },
                  }}
                  onClick={() => handleLoadScenario(s)}
                >
                  <BookmarkIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: '0.82rem', fontWeight: 600, color: 'text.primary',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {s.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {(s.tranches || []).length} tranche{(s.tranches || []).length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Tooltip title="Delete scenario">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteScenario(s.id); }}
                      sx={{ color: 'text.secondary', '&:hover': { color: '#ef4444' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Simulation error */}
      {simError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {simError}
        </Alert>
      )}

      {/* Simulation loading */}
      {simLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {/* Results */}
      {!simLoading && simResult && summary && (
        <>
          <Divider sx={{ my: 1, mb: 3 }} />

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard
              label="Spot Revenue"
              value={formatAUD(summary.total_spot_revenue)}
              color="#64748b"
            />
            <StatCard
              label="Blended Revenue"
              value={formatAUD(summary.total_blended_revenue)}
              color="#D98A2B"
            />
            <Paper
              sx={{
                px: 2.5,
                py: 2,
                flex: 1,
                minWidth: 160,
                border: '1px solid', borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                Revenue Delta
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: deltaPositive ? '#D98A2B' : '#ef4444', letterSpacing: '-0.02em' }}>
                  {deltaPositive ? '+' : ''}{formatAUD(summary.revenue_delta_aud)}
                </Typography>
                <Chip
                  label={`${deltaPositive ? '+' : ''}${summary.revenue_delta_pct.toFixed(1)}%`}
                  icon={deltaPositive ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    height: 22,
                    bgcolor: deltaPositive ? 'rgba(217, 138, 43, 0.08)' : 'rgba(239,68,68,0.08)',
                    color: deltaPositive ? '#D98A2B' : '#ef4444',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Box>
            </Paper>
            <StatCard
              label="Avg Spot Price"
              value={`A$${summary.avg_spot_price.toFixed(2)}`}
              unit="/MWh"
              color="#64748b"
            />
            <StatCard
              label="Avg Blended Price"
              value={`A$${summary.avg_blended_price.toFixed(2)}`}
              unit="/MWh"
              color="#D98A2B"
            />
            <StatCard
              label="PPA Coverage"
              value={`${summary.ppa_coverage_pct}%`}
              color={summary.ppa_coverage_pct > 100 ? '#ef4444' : '#D98A2B'}
            />
          </Box>

          {/* Total Generation */}
          <Box sx={{ mb: 3 }}>
            <Paper
              sx={{
                px: 2.5,
                py: 1.5,
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 0.5,
                border: '1px solid', borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', mr: 1 }}>
                Total Generation
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                {formatMWh(summary.total_mwh)}
              </Typography>
            </Paper>
          </Box>

          {/* Charts */}
          <ChartSection
            title="Revenue Comparison"
            subtitle="Spot-only revenue vs blended PPA + merchant revenue over time"
          >
            <RevenueComparisonChart
              data={simResult.data}
              frequency={simResult.frequency}
            />
          </ChartSection>

          <ChartSection
            title="Revenue Breakdown"
            subtitle="Stacked view of PPA-contracted revenue vs merchant (spot-exposed) revenue"
          >
            <RevenueBreakdownChart
              data={simResult.data}
              frequency={simResult.frequency}
            />
          </ChartSection>

          <ChartSection
            title="Price Comparison"
            subtitle="Effective A$/MWh under spot vs blended pricing, with PPA contract price reference"
          >
            <PriceComparisonChart
              data={simResult.data}
              frequency={simResult.frequency}
              weightedPpaPrice={weightedPpaPrice}
            />
          </ChartSection>
        </>
      )}

      {/* Empty state — no simulation yet */}
      {!simLoading && !simResult && !simError && (
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px solid', borderColor: 'divider',
            boxShadow: 'none',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              bgcolor: 'rgba(217, 138, 43, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, fontSize: '1rem' }}>
            Configure and simulate
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto', fontSize: '0.85rem' }}>
            Set up your PPA tranches above with contracted prices and coverage percentages,
            then click Simulate to compare against spot market revenue.
          </Typography>
        </Paper>
      )}

      {/* Save Scenario Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '14px', minWidth: 360 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 0.5 }}>
          Save Scenario
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 2 }}>
            Save the current PPA tranche configuration for later use.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Scenario name"
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveScenario(); }}
          />
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tranches.map((t, i) => (
              <Chip
                key={i}
                label={`${t.name}: A$${t.price}/MWh @ ${t.coverage_pct}%`}
                size="small"
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveScenario}
            disabled={!scenarioName.trim()}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RevenueAnalysis;
